<script setup lang="ts">
/**
 * A full-viewport wait, with a painting on it.
 *
 * Three illustrations (`public/art/loading-*.jpg`): the harbour at dawn for
 * the anchorage and the session restore, a destroyer in a heavy sea for the
 * battle, a battleship at dusk for anything in between. The picture is the
 * screen; over it, in kit pieces (ui/kit): the title on a navy enamel header
 * plate, the one line about what is happening in outlined game lettering, a
 * chunky progress groove that fills when the caller knows how far along it is
 * and sweeps when it does not, and a tip in a capsule on a compass that changes
 * every few seconds. Nothing else: a loading screen that competes with the
 * game it loads is the wrong way round.
 *
 * The picture fades in rather than popping. WebP with a JPEG behind it;
 * the screen is up for a second or two and the file is under 150 KB, which
 * is nothing next to the chunk it covers.
 */
import { computed } from 'vue';

import { useI18n } from '@/i18n';

import GameIcon from './kit/GameIcon.vue';

import { useLoadingTips } from './loading-tips';
import { typeInto } from './motion/typewriter';

/** The tip is typed in over this long (life-layer.md brief, item 5). */
const TIP_TYPE_MS = 900;

function typeTip(el: Element): void {
  typeInto(el as HTMLElement, TIP_TYPE_MS);
}

export type LoadingArt = 'harbour' | 'battle' | 'dusk';

const props = withDefaults(
  defineProps<{
    title?: string;
    subtitle?: string;
    art?: LoadingArt;
    /** 0…1 when known; omitted for an indeterminate sweep. */
    progress?: number | null;
    /** Show the rotating tips. Off for the session restore, which is over before one is read. */
    tips?: boolean;
    /** Show the painting at once, for a screen that replaces another showing the same one. */
    instant?: boolean;
  }>(),
  { title: 'Admiralty', subtitle: '', art: 'dusk', progress: null, tips: true, instant: false },
);

const { t } = useI18n();

const { index: tipIndex, tip } = useLoadingTips(() => props.tips);
const src = computed(() => `/art/loading-${props.art}.jpg`);
const determinate = computed(() => props.progress !== null && props.progress !== undefined);
const pct = computed(() => Math.round(Math.max(0, Math.min(1, props.progress ?? 0)) * 100));
</script>

<template>
  <div class="loading" role="status" aria-live="polite" data-test="loading-screen" :data-art="art">
    <picture class="art-wrap">
      <source :srcset="`/art/loading-${art}.webp`" type="image/webp" />
      <img class="art" :class="{ instant }" :src="src" alt="" decoding="async" fetchpriority="high" />
    </picture>
    <div class="scrim" aria-hidden="true"></div>
    <div class="fog" aria-hidden="true"></div>

    <div class="plate">
      <h1 class="k-header title">
        <GameIcon name="anchor" :size="44" />
        <span class="k-title title-text">{{ title }}</span>
      </h1>
      <p v-if="subtitle" class="subtitle k-label">{{ subtitle }}</p>
      <div class="bar" :class="{ sweep: !determinate }" :aria-valuenow="determinate ? pct : undefined" :aria-valuemin="0" :aria-valuemax="100" role="progressbar">
        <i :style="determinate ? { transform: `scaleX(${pct / 100})` } : undefined"></i>
      </div>
    </div>

    <p v-if="tips" class="tip k-chip" data-test="loading-tip">
      <GameIcon name="compass" :size="40" :label="t('loading.tipLabel')" />
      <Transition name="tip" mode="out-in" appear @enter="typeTip">
        <span :key="tipIndex" class="tip-text">{{ tip }}</span>
      </Transition>
    </p>
  </div>
</template>

<style scoped>
.loading {
  position: relative;
  display: grid;
  grid-template-rows: 1fr auto;
  height: 100%;
  min-height: 100dvh;
  overflow: hidden;
  background: var(--navy-900);
  color: var(--navy-100);
}

.art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  animation: art-in 900ms ease-out both;
}

.art.instant {
  animation: none;
}

@keyframes art-in {
  from {
    opacity: 0;
    transform: scale(1.03);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.scrim {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to top, rgba(7, 10, 14, 0.85) 0%, rgba(7, 10, 14, 0.45) 28%, rgba(7, 10, 14, 0.05) 60%, rgba(7, 10, 14, 0.2) 100%),
    linear-gradient(to right, rgba(7, 10, 14, 0.35), transparent 45%);
}

/*
 * Fog drifting over the painting: a double-wide strip of soft grey gradients
 * that slides one half-width in 20 s and starts again, seamlessly (the second
 * half repeats the first). Transform only.
 */
.fog {
  position: absolute;
  inset: 20% 0 0;
  width: 200%;
  pointer-events: none;
  opacity: 0.35;
  background:
    radial-gradient(ellipse 22% 30% at 12% 55%, rgba(210, 220, 230, 0.55), transparent 70%),
    radial-gradient(ellipse 18% 24% at 37% 70%, rgba(210, 220, 230, 0.45), transparent 70%),
    radial-gradient(ellipse 22% 30% at 62% 55%, rgba(210, 220, 230, 0.55), transparent 70%),
    radial-gradient(ellipse 18% 24% at 87% 70%, rgba(210, 220, 230, 0.45), transparent 70%);
  animation: fog-drift 20s linear infinite;
}

@keyframes fog-drift {
  to {
    transform: translateX(-50%);
  }
}

.plate {
  position: relative;
  align-self: end;
  display: grid;
  justify-items: start;
  gap: 0.6rem;
  max-width: 34rem;
  padding: 0 max(1rem, env(safe-area-inset-left));
  margin-bottom: 1rem;
}

/* The header plate shrinks to its title; the kit sets its size from `--h`. */
.title {
  --h: clamp(52px, 6vw, 68px);
  width: fit-content;
  max-width: 100%;
  margin: 0;
  filter: drop-shadow(0 4px 0 rgba(0, 0, 0, 0.4));
}

.title-text {
  font-size: clamp(1.4rem, 4vw, 2.2rem);
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subtitle {
  margin: 0 0 0 0.4rem;
  font-size: 1.05rem;
}

/* The kit's progress groove: dark, deep, a glossy amber fill scaled along X from the left. */
.bar {
  position: relative;
  width: min(100%, 22rem);
  height: 18px;
  margin-left: 0.4rem;
  border-radius: 999px;
  background: #070c12;
  box-shadow: inset 0 3px 4px rgba(0, 0, 0, 0.85), 0 0 0 2px var(--kit-ink), 0 3px 0 2px var(--kit-ink);
  overflow: hidden;
}

.bar i {
  position: absolute;
  inset: 3px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffe08a, #f0a92a 55%, #c47a12);
  box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.6);
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 300ms ease-out;
}

/* Unknown progress: a third-width fill sweeps across (transform, not left). */
.bar.sweep i {
  right: auto;
  width: calc(34% - 6px);
  transform: translateX(-100%);
  animation: sweep 1.6s ease-in-out infinite;
}

@keyframes sweep {
  to {
    transform: translateX(300%);
  }
}

.tip {
  position: relative;
  align-items: center;
  gap: 0.6rem;
  width: fit-content;
  max-width: min(40rem, calc(100% - 2rem));
  margin: 0 auto 0 max(1rem, env(safe-area-inset-left));
  margin-bottom: max(1rem, env(safe-area-inset-bottom));
  padding: 0.3rem 1.1rem 0.3rem 0.4rem;
  border-radius: 22px;
  font-family: var(--font-game-body);
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.3;
  color: #e8eef4;
}

.tip-enter-active,
.tip-leave-active {
  transition: opacity 350ms ease;
}

.tip-enter-from,
.tip-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .art,
  .fog {
    animation: none;
  }
  .bar.sweep i {
    animation: none;
    width: auto;
    right: 3px;
    transform: none;
    opacity: 0.5;
  }
  .bar i,
  .tip-enter-active,
  .tip-leave-active {
    transition: none;
  }
}

@media (min-width: 900px) {
  .plate {
    margin-bottom: 1.6rem;
    padding-left: max(3rem, env(safe-area-inset-left));
  }
  .tip {
    margin-left: max(3rem, env(safe-area-inset-left));
  }
}
</style>
